import type { AxiosError } from 'axios'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
	useCreateJobGroupCluster,
	useDeleteJobGroupCluster,
	useJobGroupClusters,
	useUpdateJobGroupCluster,
} from '@/api/queries/job-group-cluster-queries'
import {
	useCreateJobGroup,
	useDeleteJobGroup,
	useJobGroups,
	useUpdateJobGroup,
} from '@/api/queries/job-group-queries'
import {
	JobGroupClusterFormContent,
	type JobGroupClusterFormValues,
} from '@/components/administration/commitment-matrix/job-group-cluster-form'
import {
	JobGroupFormContent,
	type JobGroupFormValues,
} from '@/components/administration/commitment-matrix/job-group-form'
import { ConfirmDialog, ResponsiveFormModal } from '@/components/commons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { JobGroup, JobGroupCluster } from '@/types'

export function JobGroupsTab() {
	return (
		<Tabs defaultValue="groups">
			<TabsList className="w-fit">
				<TabsTrigger value="groups">Grupos</TabsTrigger>
				<TabsTrigger value="clusters">Clusters</TabsTrigger>
			</TabsList>

			<TabsContent value="groups" className="pt-4">
				<JobGroupsSubTab />
			</TabsContent>

			<TabsContent value="clusters" className="pt-4">
				<JobGroupClustersSubTab />
			</TabsContent>
		</Tabs>
	)
}

function JobGroupsSubTab() {
	const { data: jobGroups = [], isLoading } = useJobGroups()
	const createJobGroupMutation = useCreateJobGroup()
	const updateJobGroupMutation = useUpdateJobGroup()
	const deleteJobGroupMutation = useDeleteJobGroup()

	const [jobGroupFormOpen, setJobGroupFormOpen] = useState(false)
	const [editingJobGroup, setEditingJobGroup] = useState<JobGroup | undefined>(
		undefined,
	)
	const [deletingJobGroup, setDeletingJobGroup] = useState<JobGroup | null>(
		null,
	)

	const isSavingJobGroup =
		createJobGroupMutation.isPending || updateJobGroupMutation.isPending

	const handleOpenCreateJobGroup = () => {
		setEditingJobGroup(undefined)
		setJobGroupFormOpen(true)
	}

	const handleOpenEditJobGroup = (jobGroup: JobGroup) => {
		setEditingJobGroup(jobGroup)
		setJobGroupFormOpen(true)
	}

	const handleSaveJobGroup = (values: JobGroupFormValues) => {
		if (editingJobGroup) {
			updateJobGroupMutation.mutate(
				{ id: editingJobGroup.id, body: values },
				{
					onSuccess: () => {
						toast.success('Grupo atualizado.')
						setJobGroupFormOpen(false)
						setEditingJobGroup(undefined)
					},
					onError: (error) => {
						const message = (error as AxiosError<{ message?: string }>).response
							?.data?.message
						toast.error(message ?? 'Não foi possível atualizar o grupo.')
					},
				},
			)
		} else {
			createJobGroupMutation.mutate(values, {
				onSuccess: () => {
					toast.success('Grupo criado.')
					setJobGroupFormOpen(false)
				},
				onError: (error) => {
					const message = (error as AxiosError<{ message?: string }>).response
						?.data?.message
					toast.error(message ?? 'Não foi possível criar o grupo.')
				},
			})
		}
	}

	const handleConfirmDeleteJobGroup = () => {
		if (!deletingJobGroup) return

		deleteJobGroupMutation.mutate(deletingJobGroup.id, {
			onSuccess: () => {
				toast.success('Grupo excluído.')
				setDeletingJobGroup(null)
			},
			onError: (error) => {
				const message = (error as AxiosError<{ message?: string }>).response
					?.data?.message
				toast.error(message ?? 'Não foi possível excluir o grupo.')
				setDeletingJobGroup(null)
			},
		})
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex justify-end">
				<Button
					onClick={handleOpenCreateJobGroup}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>Novo grupo</span>
				</Button>
			</div>

			<div className="flex flex-col gap-3">
				{isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<Skeleton
							key={`job-group-skeleton-${i}`}
							className="h-20 w-full rounded-lg"
						/>
					))
				) : jobGroups.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum grupo de cargo cadastrado.
					</p>
				) : (
					jobGroups.map((jobGroup) => (
						<div
							key={jobGroup.id}
							className="rounded-lg border border-kaefer-light-silver bg-white p-4"
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div className="min-w-0">
									<span className="font-semibold text-kaefer-dark-grey">
										{jobGroup.name}
									</span>
									<div className="mt-2 flex flex-wrap gap-1.5">
										{jobGroup.jobRoles.length === 0 ? (
											<span className="text-xs text-muted-foreground">
												Nenhum cargo vinculado
											</span>
										) : (
											jobGroup.jobRoles.map((role) => (
												<Badge
													key={role}
													className="bg-kaefer-violet/15 text-kaefer-violet"
												>
													{role}
												</Badge>
											))
										)}
									</div>
								</div>

								<div className="flex items-center gap-1">
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => handleOpenEditJobGroup(jobGroup)}
										className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70"
										title="Editar grupo"
									>
										<Pencil className="size-4" />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => setDeletingJobGroup(jobGroup)}
										disabled={deleteJobGroupMutation.isPending}
										className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
										title="Excluir grupo"
									>
										<Trash2 className="size-4" />
									</Button>
								</div>
							</div>
						</div>
					))
				)}
			</div>

			<ResponsiveFormModal
				open={jobGroupFormOpen}
				onOpenChange={setJobGroupFormOpen}
				title={editingJobGroup ? 'Editar grupo' : 'Novo grupo de cargo'}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-md"
			>
				<JobGroupFormContent
					key={editingJobGroup?.id ?? 'new'}
					mode={editingJobGroup ? 'edit' : 'create'}
					initial={editingJobGroup}
					onSave={handleSaveJobGroup}
					onClose={() => setJobGroupFormOpen(false)}
					isSaving={isSavingJobGroup}
				/>
			</ResponsiveFormModal>

			<ConfirmDialog
				open={!!deletingJobGroup}
				onOpenChange={(open) => {
					if (!open) setDeletingJobGroup(null)
				}}
				title="Excluir grupo"
				onConfirm={handleConfirmDeleteJobGroup}
				isPending={deleteJobGroupMutation.isPending}
				destructive
			>
				Tem certeza que deseja excluir o grupo "{deletingJobGroup?.name}"? Esta
				ação não pode ser desfeita.
			</ConfirmDialog>
		</div>
	)
}

function JobGroupClustersSubTab() {
	const { data: jobGroupClusters = [], isLoading } = useJobGroupClusters()
	const { data: jobGroups = [] } = useJobGroups()
	const jobGroupNameById = new Map(jobGroups.map((g) => [g.id, g.name]))

	const createClusterMutation = useCreateJobGroupCluster()
	const updateClusterMutation = useUpdateJobGroupCluster()
	const deleteClusterMutation = useDeleteJobGroupCluster()

	const [clusterFormOpen, setClusterFormOpen] = useState(false)
	const [editingCluster, setEditingCluster] = useState<
		JobGroupCluster | undefined
	>(undefined)
	const [deletingCluster, setDeletingCluster] =
		useState<JobGroupCluster | null>(null)

	const isSavingCluster =
		createClusterMutation.isPending || updateClusterMutation.isPending

	const handleOpenCreateCluster = () => {
		setEditingCluster(undefined)
		setClusterFormOpen(true)
	}

	const handleOpenEditCluster = (cluster: JobGroupCluster) => {
		setEditingCluster(cluster)
		setClusterFormOpen(true)
	}

	const handleSaveCluster = (values: JobGroupClusterFormValues) => {
		if (editingCluster) {
			updateClusterMutation.mutate(
				{ id: editingCluster.id, body: values },
				{
					onSuccess: () => {
						toast.success('Cluster atualizado.')
						setClusterFormOpen(false)
						setEditingCluster(undefined)
					},
					onError: (error) => {
						const message = (error as AxiosError<{ message?: string }>).response
							?.data?.message
						toast.error(message ?? 'Não foi possível atualizar o cluster.')
					},
				},
			)
		} else {
			createClusterMutation.mutate(values, {
				onSuccess: () => {
					toast.success('Cluster criado.')
					setClusterFormOpen(false)
				},
				onError: (error) => {
					const message = (error as AxiosError<{ message?: string }>).response
						?.data?.message
					toast.error(message ?? 'Não foi possível criar o cluster.')
				},
			})
		}
	}

	const handleConfirmDeleteCluster = () => {
		if (!deletingCluster) return

		deleteClusterMutation.mutate(deletingCluster.id, {
			onSuccess: () => {
				toast.success('Cluster excluído.')
				setDeletingCluster(null)
			},
			onError: (error) => {
				const message = (error as AxiosError<{ message?: string }>).response
					?.data?.message
				toast.error(message ?? 'Não foi possível excluir o cluster.')
				setDeletingCluster(null)
			},
		})
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex justify-end">
				<Button
					onClick={handleOpenCreateCluster}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>Novo cluster</span>
				</Button>
			</div>

			<div className="flex flex-col gap-3">
				{isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<Skeleton
							key={`job-group-cluster-skeleton-${i}`}
							className="h-20 w-full rounded-lg"
						/>
					))
				) : jobGroupClusters.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum cluster cadastrado.
					</p>
				) : (
					jobGroupClusters.map((cluster) => (
						<div
							key={cluster.id}
							className="rounded-lg border border-kaefer-light-silver bg-white p-4"
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div className="min-w-0">
									<span className="font-semibold text-kaefer-dark-grey">
										{cluster.name}
									</span>
									<div className="mt-2 flex flex-wrap gap-1.5">
										{cluster.jobGroupIds.length === 0 ? (
											<span className="text-xs text-muted-foreground">
												Nenhum grupo de cargo vinculado
											</span>
										) : (
											cluster.jobGroupIds.map((id) => (
												<Badge
													key={id}
													className="bg-kaefer-violet/15 text-kaefer-violet"
												>
													{jobGroupNameById.get(id) ?? id}
												</Badge>
											))
										)}
									</div>
								</div>

								<div className="flex items-center gap-1">
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => handleOpenEditCluster(cluster)}
										className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70"
										title="Editar cluster"
									>
										<Pencil className="size-4" />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-circle"
										onClick={() => setDeletingCluster(cluster)}
										disabled={deleteClusterMutation.isPending}
										className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
										title="Excluir cluster"
									>
										<Trash2 className="size-4" />
									</Button>
								</div>
							</div>
						</div>
					))
				)}
			</div>

			<ResponsiveFormModal
				open={clusterFormOpen}
				onOpenChange={setClusterFormOpen}
				title={editingCluster ? 'Editar cluster' : 'Novo cluster'}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-md"
			>
				<JobGroupClusterFormContent
					key={editingCluster?.id ?? 'new'}
					mode={editingCluster ? 'edit' : 'create'}
					initial={editingCluster}
					onSave={handleSaveCluster}
					onClose={() => setClusterFormOpen(false)}
					isSaving={isSavingCluster}
				/>
			</ResponsiveFormModal>

			<ConfirmDialog
				open={!!deletingCluster}
				onOpenChange={(open) => {
					if (!open) setDeletingCluster(null)
				}}
				title="Excluir cluster"
				onConfirm={handleConfirmDeleteCluster}
				isPending={deleteClusterMutation.isPending}
				destructive
			>
				Tem certeza que deseja excluir o cluster "{deletingCluster?.name}"? Esta
				ação não pode ser desfeita.
			</ConfirmDialog>
		</div>
	)
}
